import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "web-quotes-view__header" }
const _hoisted_2 = { class: "web-quotes-view__content" }
const _hoisted_3 = { class: "web-quotes-view__tables" }
const _hoisted_4 = { class: "tables__table-header" }
const _hoisted_5 = { class: "main-table" }
const _hoisted_6 = { class: "tables__main-table" }
const _hoisted_7 = { class: "main-table" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "tables__pagination-buttons" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "web-quotes-view__footer" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "metrics" }
const _hoisted_20 = { class: "aliceblue fira" }
const _hoisted_21 = { class: "aliceblue fira" }
const _hoisted_22 = { class: "webQuote__header" }
const _hoisted_23 = { class: "semi-large" }
const _hoisted_24 = ["href"]
const _hoisted_25 = { class: "webQuote__info" }
const _hoisted_26 = ["disabled"]
const _hoisted_27 = ["disabled"]
const _hoisted_28 = ["disabled"]
const _hoisted_29 = ["value"]
const _hoisted_30 = { class: "webQuote__items-table" }
const _hoisted_31 = { class: "items-table" }
const _hoisted_32 = { class: "secondary-text" }
const _hoisted_33 = { class: "webQuote__actions" }
const _hoisted_34 = ["disabled"]
const _hoisted_35 = ["disabled"]
const _hoisted_36 = ["disabled"]
const _hoisted_37 = { class: "login-form-fields" }
const _hoisted_38 = { class: "login-modal__actions" }
const _hoisted_39 = ["disabled"]
const _hoisted_40 = ["disabled"]
const _hoisted_41 = { class: "login-modal__operationInfo" }
const _hoisted_42 = { key: 0 }
const _hoisted_43 = {
  key: 1,
  style: {"color":"red"}
}

import { useWebQuoteManager } from '@/services/webQuoteManager';
import { useFormatters } from '@/util/formatters';
import { SalesOrder } from '@/types/salesOrder';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { getAuthContext } from '@/services/auth';
import BaseModal from '@/components/modals/BaseModal.vue';
import { useApiEnvStore } from '@/services/env';

//--------------------------------------------------
// Service Initialization
//--------------------------------------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'WebQuotesView',
  setup(__props) {

//--------------------------------------------------
// Imports & Composables
//--------------------------------------------------
const webQ = useWebQuoteManager();
const { formatCurrency } = useFormatters();
const envStore = useApiEnvStore();

//--------------------------------------------------
// Reactive State
//--------------------------------------------------
const salesOrderDescription = ref<string>('');
const baid = ref<string>('');
const poNumber = ref<string>('');

const loginUsername = ref<string>('');
const loginPassword = ref<string>('');

//--------------------------------------------------
// Watchers
//--------------------------------------------------
watch(
	() => webQ.selectedWebQuote.value,
	(newValue) => {
		if (!newValue) {
			salesOrderDescription.value = '';
			baid.value = '';
			poNumber.value = '';
			return;
		}
		salesOrderDescription.value = `WebQuote#${newValue.id}`;
	}
);

//--------------------------------------------------
// UI / Process State
//--------------------------------------------------
const isRunning = ref<boolean>(false);
const isLoggingIn = ref<boolean>(false);
const loginOperation = ref<boolean>(false);
const loginFailed = ref<boolean>(false);

function closeSlideOutModal() {
	if (isRunning.value) return;
	webQ.clearSelectedWebQuote();
}

function closeLoginModal() {
	if (isRunning.value) return;
	isLoggingIn.value = false;
	loginUsername.value = '';
	loginPassword.value = '';
	loginFailed.value = false;
	loginOperation.value = false;
}

//--------------------------------------------------
// Sales Order Creation Flow
//--------------------------------------------------
async function createSalesOrder() {
	// ==============================
	// Pre-Process Validation
	// ==============================
	// Ensure web quote is selected
	if (!webQ.selectedWebQuote.value) return;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// Ensure non-empty form values
	if (!baid.value || !poNumber.value) {
		alert("'BAID' and 'PO Number' cannot be empty");
		isRunning.value = false;
		return;
	}

	// ==============================
	// UI Update
	// ==============================
	isRunning.value = true;

	// ==============================
	// Server Call
	// ==============================
	const salesOrder: SalesOrder = {
		baid: baid.value,
		poNumber: poNumber.value,
		description: salesOrderDescription.value,
		items: webQ.selectedWebQuote.value.items
	};

	// Attempt sales order creation
	let createSalesOrderResponseJson: any;
	let soCreated: string | null = null;
	let userVerified: boolean = false;
	try {
		const createSalesOrderResponse = await fetch(`${envStore.apiBaseUrl}/CreateSalesOrder`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${idToken}`
			},
			body: JSON.stringify({
				uid: userId,
				salesOrder
			})
		});
		if (!createSalesOrderResponse.ok) {
			throw new Error('Network response was not ok');
		}
		createSalesOrderResponseJson = await createSalesOrderResponse.json();
		soCreated = createSalesOrderResponseJson.salesOrderCreated;
		userVerified = createSalesOrderResponseJson.userVerified;
	} catch (error) {
		alert('Request cannot be processed: network error');
		isRunning.value = false;
		return;
	}

	// ==============================
	// Post-Process UI Updates
	// ==============================
	// Alert user of success if sales order number was found
	if (soCreated) {
		alert('Sales Order Created');
		isRunning.value = false;
		webQ.clearSelectedWebQuote();
		isRunning.value = false;
		return;
	}

	// Alert user if there was an issue with creating the sales order
	else if (userVerified && !soCreated) {
		alert('Request cannot be processed: sales order creation failed');
		isRunning.value = false;
		isRunning.value = false;
		return;
	}

	// Alert user if they are not verified to create sales orders
	else if (!userVerified) {
		alert('Acumatica session invalid. Please re-authenticate.');
		isLoggingIn.value = true;
		isRunning.value = false;
		return;
	}
}
//--------------------------------------------------
// Export to CSV Flow
//--------------------------------------------------
async function exportCsv() {
	// ==============================
	// Pre-Process Validation
	// ==============================
	// Ensure web quote is selected
	if (!webQ.selectedWebQuote.value) return;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// ==============================
	// UI Update
	// ==============================
	isRunning.value = true;

	// ==============================
	// Server Call
	// ==============================
	const items: SalesOrderItem = webQ.selectedWebQuote.value.items;

	// ==============================
	// Preparing CSV
	// ==============================
	const headers = Object.keys(items[0]);
	const rows = items.map((item) => headers.map((header) => escapeCsv(item[header])).join(','));
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.download = `${webQ.selectedWebQuote.value.id}.csv`;
	link.click();

	URL.revokeObjectURL(url);

	// ==============================
	// Post-Process UI Updates
	// ==============================
	// Alert user of success if sales order number was found
	alert('Process complete');
	isRunning.value = false;
	webQ.clearSelectedWebQuote();
	isRunning.value = false;
	return;
}

function escapeCsv(value) {
	if (value == null) return '';

	const string = String(value);

	if (string.includes(',') || string.includes('"') || string.includes('\n')) {
		return `"${string.replaceAll('"', '""')}"`;
	}

	return string;
}

//--------------------------------------------------
// Login To Acumatica Flow
//--------------------------------------------------
async function loginToAcumatica() {
	// Clear UI state
	loginFailed.value = false;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// Ensure username and password are provided
	if (!loginUsername.value || !loginPassword.value) {
		alert('Request cannot be processed: missing username or password.');
		isRunning.value = false;
		isRunning.value = false;
		return;
	}

	// Lock UI
	isRunning.value = true;
	loginOperation.value = true;

	// Attempt login to Acumatica
	let loginSuccess: boolean = false;
	try {
		const loginToAcumaticaResponse = await fetch(`${envStore.apiBaseUrl}/LoginToAcumatica`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${idToken}`
			},
			body: JSON.stringify({
				uid: userId,
				username: loginUsername.value,
				password: loginPassword.value
			})
		});
		if (!loginToAcumaticaResponse.ok) {
			throw new Error('Network response was not ok');
		}

		const loginToAcumaticaResponseJson = await loginToAcumaticaResponse.json();
		loginSuccess = loginToAcumaticaResponseJson.loginSuccess;
	} catch {
		alert('Request cannot be processed: network error');
		isRunning.value = false;
		loginOperation.value = false;
		return;
	}

	if (loginSuccess) {
		alert('Re-authentication successful. Please try creating the sales order again.');
		isRunning.value = false;
		closeLoginModal();
	} else {
		alert('Re-authentication failed. Please check your credentials and try again.');
		isRunning.value = false;
		loginOperation.value = false;
		loginFailed.value = true;
	}
}

//--------------------------------------------------
// Lifecycle Hooks
//--------------------------------------------------
onMounted(async () => {
	window.addEventListener('beforeunload', handleUnload);
	await webQ.fetchWebQuotes();
});

onBeforeUnmount(async () => {
	window.removeEventListener('beforeunload', handleUnload);
});

//--------------------------------------------------
// Navigation Guards
//--------------------------------------------------
onBeforeRouteLeave((to, from) => {
	if (isRunning.value) {
		return confirm('A process is still running. Are you sure you want to leave?');
	}
});

//--------------------------------------------------
// Window Event Handlers
//--------------------------------------------------
function handleUnload(event: BeforeUnloadEvent) {
	if (isRunning.value) {
		event.preventDefault();
		event.returnValue = '';
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: "web-quotes-view",
      style: _normalizeStyle({ cursor: isRunning.value ? 'wait' : 'default' })
    }, [
      _createElementVNode("div", _hoisted_1, [
        _cache[16] || (_cache[16] = _createElementVNode("div", { class: "header__titles" }, [
          _createElementVNode("h1", { class: "fira aliceblue" }, "Web Quotes"),
          _createElementVNode("p", { class: "fira aliceblue semi-large" }, "Qteedy Web Quotes")
        ], -1)),
        _withDirectives(_createElementVNode("input", {
          placeholder: "Search...",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(webQ).searchQuery.value) = $event))
        }, null, 512), [
          [_vModelText, _unref(webQ).searchQuery.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("table", _hoisted_5, [
              _cache[17] || (_cache[17] = _createElementVNode("colgroup", null, [
                _createElementVNode("col", { style: {"width":"75px"} }),
                _createElementVNode("col", { style: {"min-width":"200px","width":"auto"} }),
                _createElementVNode("col", { style: {"width":"200px"} }),
                _createElementVNode("col", { style: {"width":"200px"} })
              ], -1)),
              _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(webQ).tableHeadings, (heading) => {
                    return (_openBlock(), _createElementBlock("th", { key: heading }, _toDisplayString(heading), 1))
                  }), 128))
                ])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("table", _hoisted_7, [
              _cache[18] || (_cache[18] = _createElementVNode("colgroup", null, [
                _createElementVNode("col", { style: {"width":"75px"} }),
                _createElementVNode("col", { style: {"width":"auto"} }),
                _createElementVNode("col", { style: {"width":"200px"} }),
                _createElementVNode("col", { style: {"width":"200px"} })
              ], -1)),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(webQ).rowData.value, (row) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: row[0],
                    class: _normalizeClass({ selected: row[0] === _unref(webQ).selectedWebQuoteId.value }),
                    onClick: ($event: any) => (_unref(webQ).selectWebQuote(row[0]))
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (cell, cellIndex) => {
                      return (_openBlock(), _createElementBlock("td", { key: cellIndex }, _toDisplayString(cell), 1))
                    }), 128))
                  ], 10, _hoisted_8))
                }), 128))
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("button", {
              disabled: _unref(webQ).isFirstPage.value,
              onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(webQ).firstPage && _unref(webQ).firstPage(...args))),
              class: "icon-button"
            }, [...(_cache[19] || (_cache[19] = [
              _createElementVNode("span", { class: "material-symbols-outlined" }, "first_page", -1)
            ]))], 8, _hoisted_10),
            _createElementVNode("button", {
              disabled: _unref(webQ).isFirstPage.value,
              onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(webQ).prevPage && _unref(webQ).prevPage(...args))),
              class: "icon-button"
            }, [...(_cache[20] || (_cache[20] = [
              _createElementVNode("span", { class: "material-symbols-outlined" }, "chevron_left", -1)
            ]))], 8, _hoisted_11),
            _createElementVNode("p", null, _toDisplayString(_unref(webQ).currentPage), 1),
            _createElementVNode("button", {
              disabled: _unref(webQ).isLastPage.value,
              onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(webQ).nextPage && _unref(webQ).nextPage(...args))),
              class: "icon-button"
            }, [...(_cache[21] || (_cache[21] = [
              _createElementVNode("span", { class: "material-symbols-outlined" }, "chevron_right", -1)
            ]))], 8, _hoisted_12),
            _createElementVNode("button", {
              disabled: _unref(webQ).isLastPage.value,
              onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(webQ).lastPage && _unref(webQ).lastPage(...args))),
              class: "icon-button"
            }, [...(_cache[22] || (_cache[22] = [
              _createElementVNode("span", { class: "material-symbols-outlined" }, "last_page", -1)
            ]))], 8, _hoisted_13)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("button", {
          disabled: _unref(webQ).filterBy.value === _unref(webQ).FilterByValue.all,
          onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(webQ).setFilterBy(_unref(webQ).FilterByValue.all)))
        }, "View: All", 8, _hoisted_15),
        _createElementVNode("button", {
          disabled: _unref(webQ).filterBy.value === _unref(webQ).FilterByValue.last_1,
          onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(webQ).setFilterBy(_unref(webQ).FilterByValue.last_1)))
        }, " View: Last 24 Hours ", 8, _hoisted_16),
        _createElementVNode("button", {
          disabled: _unref(webQ).filterBy.value === _unref(webQ).FilterByValue.last_7,
          onClick: _cache[7] || (_cache[7] = ($event: any) => (_unref(webQ).setFilterBy(_unref(webQ).FilterByValue.last_7)))
        }, " View: Last 7 Days ", 8, _hoisted_17),
        _createElementVNode("button", {
          disabled: _unref(webQ).filterBy.value === _unref(webQ).FilterByValue.last_30,
          onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(webQ).setFilterBy(_unref(webQ).FilterByValue.last_30)))
        }, " View: Last 30 Days ", 8, _hoisted_18),
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("p", _hoisted_20, "Count: " + _toDisplayString(_unref(webQ).filteredWebQuotesCount.value), 1),
          _createElementVNode("p", _hoisted_21, "Value: " + _toDisplayString(_unref(formatCurrency)(_unref(webQ).filteredWebQuotesValue.value)), 1)
        ])
      ])
    ], 4),
    (_unref(webQ).selectedWebQuote.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "slideOutModal-backdrop",
          onClick: closeSlideOutModal,
          style: _normalizeStyle({ cursor: isRunning.value ? 'wait' : 'default' })
        }, [
          _createElementVNode("div", {
            class: "slideOutModal-container",
            onClick: _cache[13] || (_cache[13] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_22, [
              _createElementVNode("p", _hoisted_23, [
                _cache[23] || (_cache[23] = _createTextVNode(" Web Quote: ", -1)),
                _createElementVNode("a", {
                  href: _unref(webQ).selectedWebQuote.value.link,
                  target: "_blank"
                }, _toDisplayString(_unref(webQ).selectedWebQuote.value.id), 9, _hoisted_24)
              ])
            ]),
            _createElementVNode("div", _hoisted_25, [
              _cache[24] || (_cache[24] = _createElementVNode("p", { class: "field-header" }, "Customer:", -1)),
              _createElementVNode("p", null, _toDisplayString(_unref(webQ).selectedWebQuote.value.customer), 1),
              _cache[25] || (_cache[25] = _createElementVNode("p", { class: "field-header" }, "Total Value:", -1)),
              _createElementVNode("p", null, _toDisplayString(_unref(formatCurrency)(_unref(webQ).selectedWebQuote.value.total)), 1),
              _cache[26] || (_cache[26] = _createElementVNode("label", {
                for: "baid",
                class: "field-header"
              }, "BAID:", -1)),
              _withDirectives(_createElementVNode("input", {
                id: "baid",
                class: "modal",
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((baid).value = $event)),
                disabled: isRunning.value
              }, null, 8, _hoisted_26), [
                [_vModelText, baid.value]
              ]),
              _cache[27] || (_cache[27] = _createElementVNode("label", {
                for: "po",
                class: "field-header"
              }, "PO Number:", -1)),
              _withDirectives(_createElementVNode("input", {
                id: "po",
                class: "modal",
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((poNumber).value = $event)),
                disabled: isRunning.value
              }, null, 8, _hoisted_27), [
                [_vModelText, poNumber.value]
              ]),
              _cache[28] || (_cache[28] = _createElementVNode("label", {
                for: "desc",
                class: "field-header"
              }, "Description:", -1)),
              _withDirectives(_createElementVNode("input", {
                id: "desc",
                class: "modal",
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((salesOrderDescription).value = $event)),
                disabled: isRunning.value
              }, null, 8, _hoisted_28), [
                [_vModelText, salesOrderDescription.value]
              ]),
              _cache[29] || (_cache[29] = _createElementVNode("label", {
                for: "notes",
                class: "field-header"
              }, "Qteedy Notes:", -1)),
              _createElementVNode("textarea", {
                id: "notes",
                disabled: "",
                value: _unref(webQ).selectedWebQuote.value.notes ?? '',
                style: _normalizeStyle({ cursor: isRunning.value ? 'wait' : 'text' })
              }, null, 12, _hoisted_29)
            ]),
            _cache[33] || (_cache[33] = _createElementVNode("p", { class: "field-header fira" }, "Items:", -1)),
            _createElementVNode("div", _hoisted_30, [
              _createElementVNode("table", _hoisted_31, [
                _cache[31] || (_cache[31] = _createElementVNode("colgroup", null, [
                  _createElementVNode("col", { style: {"width":"62%"} }),
                  _createElementVNode("col", { style: {"width":"15%"} }),
                  _createElementVNode("col", { style: {"width":"23%"} })
                ], -1)),
                _cache[32] || (_cache[32] = _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", null, "Inventory ID"),
                    _createElementVNode("th", null, "Qty"),
                    _createElementVNode("th", null, "Disc (%)")
                  ])
                ], -1)),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(webQ).selectedWebQuote.value.items, (item) => {
                    return (_openBlock(), _createElementBlock("tr", { key: item }, [
                      _createElementVNode("td", null, [
                        _createTextVNode(_toDisplayString(item.inventoryId), 1),
                        _cache[30] || (_cache[30] = _createElementVNode("br", null, null, -1)),
                        _createElementVNode("span", _hoisted_32, _toDisplayString(item.variantTitle), 1)
                      ]),
                      _createElementVNode("td", null, _toDisplayString(item.quantity), 1),
                      _createElementVNode("td", null, _toDisplayString(Math.round(item.discountPercent * 10) / 10), 1)
                    ]))
                  }), 128))
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_33, [
              _createElementVNode("button", {
                disabled: isRunning.value,
                class: "light",
                onClick: createSalesOrder
              }, "Create SO", 8, _hoisted_34),
              _createElementVNode("button", {
                disabled: isRunning.value,
                class: "light",
                onClick: _cache[12] || (_cache[12] = ($event: any) => (_unref(webQ).clearSelectedWebQuote()))
              }, "Close", 8, _hoisted_35),
              _createElementVNode("button", {
                disabled: isRunning.value,
                class: "light",
                onClick: exportCsv
              }, "Export to CSV", 8, _hoisted_36)
            ])
          ])
        ], 4))
      : _createCommentVNode("", true),
    (isLoggingIn.value)
      ? (_openBlock(), _createBlock(BaseModal, {
          key: 1,
          onClose: closeLoginModal
        }, {
          default: _withCtx(() => [
            _createElementVNode("form", {
              class: "login-modal",
              onSubmit: _withModifiers(loginToAcumatica, ["prevent"])
            }, [
              _cache[36] || (_cache[36] = _createElementVNode("p", null, [
                _createElementVNode("span", { style: {"display":"block"} }, [
                  _createElementVNode("b", null, "Login To Acumatica")
                ]),
                _createElementVNode("span", null, [
                  _createElementVNode("i", null, "Your user session has expired or does not exist.")
                ])
              ], -1)),
              _createElementVNode("div", _hoisted_37, [
                _cache[34] || (_cache[34] = _createElementVNode("label", { for: "username" }, "Username:", -1)),
                _withDirectives(_createElementVNode("input", {
                  class: "modal",
                  id: "username",
                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((loginUsername).value = $event))
                }, null, 512), [
                  [_vModelText, loginUsername.value]
                ]),
                _cache[35] || (_cache[35] = _createElementVNode("label", { for: "password" }, "Password:", -1)),
                _withDirectives(_createElementVNode("input", {
                  class: "modal",
                  id: "password",
                  type: "password",
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((loginPassword).value = $event))
                }, null, 512), [
                  [_vModelText, loginPassword.value]
                ])
              ]),
              _createElementVNode("div", _hoisted_38, [
                _createElementVNode("button", {
                  class: "light",
                  type: "submit",
                  disabled: isRunning.value
                }, "Login", 8, _hoisted_39),
                _createElementVNode("button", {
                  class: "light",
                  onClick: closeLoginModal,
                  disabled: isRunning.value
                }, "Cancel", 8, _hoisted_40)
              ]),
              _createElementVNode("div", _hoisted_41, [
                (loginOperation.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_42, "Logging in to Acumatica..."))
                  : _createCommentVNode("", true),
                (loginFailed.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_43, "Login failed. Please try again."))
                  : _createCommentVNode("", true)
              ])
            ], 32)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})